import React, { useEffect, useState } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import {
  CircleBoldedErrorIcon,
  CircleInfoIcon,
  CircleSuccessIcon,
  TriangleWarningIcon,
  UndoIcon
} from '../../scripts/icons';

import styles from './Toast.module.scss';
import { hideToast, toastAction } from './toastSlice';
import { Toast as ToastItem, ToastType } from './toastTypes';

import { useTypedSelector } from 'state';

const FORCE_DURATION = 5000;

const toastIconMap: Record<
  ToastType,
  React.JSXElementConstructor<{
    className: string;
  }>
> = {
  error: CircleBoldedErrorIcon,
  info: CircleInfoIcon,
  success: CircleSuccessIcon,
  warning: TriangleWarningIcon
};

export const Toast = () => {
  const { queue } = useTypedSelector(state => state.toast);

  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<ToastItem>(null);

  const { message, showAction, type, actionLabel = 'Undo' } = toast || {};

  const dispatch = useDispatch();

  useEffect(() => {
    if (queue.length > 0) {
      setToast(queue[0]);
      setOpen(true);
    }
  }, [queue]);

  const onOpenChange = (open: boolean) => {
    setOpen(open);

    if (!open) {
      dispatch(hideToast());
    }
  };

  const onAction = () => {
    dispatch(toastAction());
    setOpen(false);
  };

  const onMouseLeave = () => {
    setTimeout(() => {
      if (open) {
        setOpen(false);
      }
    }, FORCE_DURATION);
  };

  const Icon = toastIconMap[type];

  return (
    <ToastPrimitive.Provider>
      <ToastPrimitive.Root
        className={classNames(styles.root, styles[type])}
        open={open}
        onOpenChange={onOpenChange}
        onMouseLeave={onMouseLeave}
      >
        <ToastPrimitive.Description className={styles.description}>
          {Icon && <Icon className={styles.icon} />}
          <span dangerouslySetInnerHTML={{ __html: message }} />
        </ToastPrimitive.Description>
        {showAction && (
          <ToastPrimitive.Action altText="Undo" asChild>
            <span className={styles.undo} onClick={onAction}>
              {actionLabel}
              <UndoIcon className={styles.undoIcon} />
            </span>
          </ToastPrimitive.Action>
        )}
      </ToastPrimitive.Root>

      <ToastPrimitive.Viewport className={styles.viewport} />
    </ToastPrimitive.Provider>
  );
};
